import { type JanusCtx, type Row, ecId, handBack, requiredInt, scalar } from '../context.js';
import { type JanusInput } from '../janus.js';
import { carrierHasTrigger, layerListEntryUuid } from '../statuses/status-trigger-gate.js';
import { statCreature } from '../summon-caster-cantrips.js';
import { replaceFormulaClassLevels } from '../../compile/formula-variable-resolution.js';
import { rollTypeModifierStacks } from '../skill-roll.js';
import { rollTypeStatusGuard } from './roll-type-status-guard.js';
import contract from '../../compile/c1-contract.json';

const CLASS_LEVEL_TAGS: ReadonlySet<string> = new Set(contract.rollup.classes.map(entry => `[${entry.variable_name}_level]`));
const ESSENCE_TAGS: ReadonlySet<string> = new Set(['[body]', '[mind]', '[soul]', '[spirit]', '[fortune]']);

/** discern_variable_tags subset shared by Temp V and Summon dice, die and modifier. Omitted lean zeros are zero;
 * derived inputs must be held. Unknown formulae keep the lane's existing named refusal. */
export function rollTypeVariable(ctx: JanusCtx, input: JanusInput, caster: Row, raw: unknown, lane: string): number {
  const value = scalar(raw);
  const tag = typeof value === 'string' ? value : '';
  let resolved: unknown = value ?? 0;
  if (CLASS_LEVEL_TAGS.has(tag)) {
    resolved = replaceFormulaClassLevels(statCreature(ctx, caster, input.abilityId), tag, contract.rollup);
    if (resolved === tag) handBack(`${lane}:${tag.slice(1, -1)}-not-held`);
  } else if (tag === '[total_level]' || tag === '[spellcasting_essence]' || ESSENCE_TAGS.has(tag)) {
    const key = tag.slice(1, -1);
    const source = tag === '[total_level]' || tag === '[spellcasting_essence]' ? statCreature(ctx, caster, input.abilityId) : caster;
    resolved = ctx.read.value(source, tag === '[spellcasting_essence]' ? 'spellcasting_essence_mod' : key);
    if (resolved == null) handBack(`${lane}:${key}-not-held`);
  }
  if (!/^-?\d+$/.test(String(resolved))) handBack(`${lane}:variable-dice-or-mod`);
  return requiredInt(resolved, `${lane}:variable-dice-or-mod`);
}

/** janus_accumulate_roll_type_adv_dis: the used item's axes plus caster sources and target against axes.
 * Only caster statuses supply next-roll hints/use-up; against extremes are inert in these two PHP rolls. */
export function rollTypeRollStacks(ctx: JanusCtx, ability: Row, caster: Row, target: Row, rollType: string, lane: string): number {
  const namesRoll = (entries: unknown[]): boolean => entries.some(entry => layerListEntryUuid(entry) === rollType);
  rollTypeStatusGuard({ ...ctx, read: { ...ctx.read, entries: (row, key) => {
    const entries = ctx.read.entries(row, key);
    if (key === 'triggers_when_caster_makes_roll_type' && namesRoll(entries)) handBack(`${lane}:roll-type-trigger-unmodelled`);
    return key === 'roll_type_adv' || key === 'roll_type_dis' || key.startsWith('roll_type_against_') ? [] : entries;
  } } }, lane, rollType, [caster]);
  if (carrierHasTrigger(ctx, caster, 'triggers_when_caster_makes_roll_type', `${lane}:roll-type-trigger-unmodelled`, rollType))
    handBack(`${lane}:roll-type-trigger-unmodelled`);
  return rollTypeSourceStacks(ctx, ability, caster, target, rollType);
}

/** Standing item/caster/against sources, shared with damage's own roll-hook pipeline. */
export function rollTypeItemStacks(ability: Row, rollType: string, entries: (row: Row, key: string) => unknown[]): number {
  const lists = (key: string): boolean => entries(ability, key).some(entry => layerListEntryUuid(entry) === rollType);
  return Number(lists('roll_type_adv')) - Number(lists('roll_type_dis'));
}

export function rollTypeSourceStacks(ctx: JanusCtx, ability: Row, caster: Row, target: Row, rollType: string, fragmentItemId?: string): number {
  const itemDelta = rollTypeItemStacks(ability, rollType, ctx.read.entries);
  const entitiesDelta = rollTypeModifierStacks(ctx, caster, [rollType], ['adv', 'dis'],
    { usedItemType: '', usedRollType: rollType, rollTo: ecId(target), opposing: target, fragmentItemId })
    + rollTypeModifierStacks(ctx, target, [rollType], ['against_adv', 'against_dis'],
      { usedItemType: '', usedRollType: rollType, rollTo: ecId(caster), opposing: caster, fragmentItemId });
  return Math.max(-5, Math.min(5, itemDelta + entitiesDelta));
}
